<template>
    <MainViewContainer :showBackIcon="routerUserId != stateUserId">

        <div class="col-span-12 mb-6">
            <h1 v-if="routerUserId == stateUserId">
                {{ $t('default.dashboard.greetings', { name: userName }) }}
            </h1>
            <h1 v-else>
                {{
                    orgUserName ?
                        $t('default.user_results.answered_surveys', { name: orgUserName }) :
                        $t('default.user_results.no_answered_surveys')
                }}
            </h1>
        </div>

        <div class="col-span-12 md:col-span-6 lg:col-span-5">
            <h2 v-if="routerUserId == stateUserId">
                {{ $t('default.user_results.your_surveys') }}
            </h2>

            <UserAnsweredSurveys :userId="routerUserId">
                <template #empty>
                    {{
                        routerUserId == stateUserId ? $t('default.user_results.no_survey') :
                            $t('default.user_results.no_survey_for_admin')
                    }}
                </template>
            </UserAnsweredSurveys>

            <template v-if="routerUserId == stateUserId">
                <h2 class="mt-20">
                    {{ $t('default.user_results.received_invitations') }}
                </h2>
                <table class="shadow-lg bg-white table-auto w-full" v-if="invitations.length">
                    <thead>
                        <tr class="font-bold text-lg">
                            <th class=" bg-gray-100 text-left px-8 py-4">{{
                                $t('default.dashboard.survey_table_name') }}
                            </th>
                            <th class="bg-gray-100"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(inv, i) in invitations" :key="i" :class="i % 2 ? 'bg-gray-100' : ''">
                            <td class="px-8 py-4">{{ inv.survey_name }}</td>
                            <td class="px-2 py-2 md:px-8 md:py-4 text-center w-2/5">
                                <router-link :to="inv.link"
                                    class="font-bold text-green hover:text-green-dark cursor-pointer">
                                    {{ $t('default.user_results.answer') }}
                                </router-link>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else>
                    {{ $t('default.user_results.no_tokens') }}
                </p>
            </template>

            <template v-if="routerUserId == stateUserId && materialPages.length > 0">
                <h2 class="mt-20">
                    {{ $t('default.user_results.material_bank') }}
                </h2>
                <MaterialPageList :pages="materialPages" />
            </template>
        </div>
        <div class="col-span-12 mt-10 md:mt-0 md:col-start-8 md:col-span-5 lg:col-span-4 lg:col-start-8 ">
            <template v-if="routerUserId == stateUserId">
                <h2>
                    <span class="mr-4">
                        <font-awesome-icon icon="fa-solid fa-comments" />
                    </span>
                    {{ $t('default.user_results.latest_discussions') }}
                </h2>
                <table class="shadow-lg bg-white table-auto w-full" v-if="latestDiscussionEntries.length > 0">
                    <tbody>
                        <tr v-for="(entry, i) in latestDiscussionEntries" :key="entry.id"
                            :class="i % 2 ? 'bg-gray-100' : ''">
                            <td class="px-8 py-4">
                                <div class="flex justify-between items-baseline">
                                    <router-link :to="entry.to"
                                        class="font-bold text-green hover:text-green-dark cursor-pointer">
                                        {{ entry.title }}
                                    </router-link>
                                    <span class="text-xs text-black-500">{{ formatDate(entry.date) }}</span>
                                </div>
                                <p class="text-sm text-black-900">{{ entry.preview }}</p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p v-else>{{ $t('default.user_results.no_discussions_available_yet') }}</p>
            </template>

            <h2 class="mt-20">
                <span class="mr-4">
                <font-awesome-icon icon="fa-solid fa-list-check" />
                </span>
                {{ $t('default.user_results.your_tasks_to_do') }}
            </h2>
            <div v-if="userTaskRows && userTaskRows.length > 0">
                <SimpleTable :headings="[]" :rows="userTaskRows" />
            </div>
            <p v-else>{{ $t("default.user_results.no_tasks_available_yet") }}</p>

            <h2 class="mt-20">
                <span class="mr-4">
                <font-awesome-icon icon="fa-solid fa-check-circle" />
                </span>
                {{ $t('default.user_results.active_goals') }}
            </h2>
            <div v-if="userGoalRows && userGoalRows.length > 0">
                <SimpleTable :headings="[]" :rows="userGoalRows" />
            </div>
            <p v-else>{{ $t("default.user_results.no_goals_available_yet") }}</p>

        </div>
    </MainViewContainer>
    <template v-if="state.openedGoalOrTask != null">
        <GoalModal v-if="state.openedGoalOrTask.goal" :userGoal="state.openedGoalOrTask"
            @close="state.openedGoalOrTask = null" />
        <TaskModal v-else :userTask="state.openedGoalOrTask" @close="state.openedGoalOrTask = null" />
    </template>
</template>

<script>
import MainViewContainer from '@/components/MainViewContainer'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import SimpleTable from '@/components/SimpleTable'
import GoalModal from '@/components/GoalModal'
import TaskModal from '@/components/TaskModal'
import UserAnsweredSurveys from '@/components/user-results/UserAnsweredSurveys.vue'
import MaterialPageList from '@/components/material-bank/MaterialPageList.vue'
import translator from '../mixins/translator'
import { stringToDatetime } from '../datetimeUtils'
import { useStore } from 'vuex'
import { computed, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'
const INCOMPLETE_CHECKBOX = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.3869 3 12 3C9.61305 3 7.32387 3.94821 5.63604 5.63604C3.94821 7.32387 3 9.61305 3 12C3 13.1819 3.23279 14.3522 3.68508 15.4442C4.13738 16.5361 4.80031 17.5282 5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442Z" fill="#F2F2F2"/>
    <path d="M9 12L11 14L15 10M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`

const COMPLETED_CHECKBOX = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.3869 3 12 3C9.61305 3 7.32387 3.94821 5.63604 5.63604C3.94821 7.32387 3 9.61305 3 12C3 13.1819 3.23279 14.3522 3.68508 15.4442C4.13738 16.5361 4.80031 17.5282 5.63604 18.364C6.47177 19.1997 7.46392 19.8626 8.55585 20.3149C9.64778 20.7672 10.8181 21 12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442Z" fill="#00D1B5"/>
    <path d="M9 12L11 14L15 10M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z" stroke="#E0E0E0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`

export default {

    components: {
        MainViewContainer,
        SimpleTable,
        TaskModal,
        GoalModal,
        FontAwesomeIcon,
        UserAnsweredSurveys,
        MaterialPageList,
    },
    setup() {
        const store = useStore()
        const route = useRoute()
        const routerUserId = route.params.usr
        // org id will be defined when arriving through the organization members
        // page
        const orgId = route.params.org
        const state = reactive({
            user: computed(() => store.state.user.user),
            userAnswers: computed(() => store.state.user.userAnswers),
            userGoals: computed(() => store.state.tasksGoals.userGoals),
            userTasks: computed(() => store.state.tasksGoals.userTasks),
            openedGoalOrTask: null,
        })
        const materialPages = computed(() => store.state.materialBank.pages)

        onMounted(() => {
            store.dispatch('user/fetchUserSurveyTokens', { usr: routerUserId })
            store.dispatch('tasksGoals/fetchUserTasks', { usr: routerUserId })
            store.dispatch('tasksGoals/fetchUserGoals', { usr: routerUserId })
            if (orgId !== undefined) {
                store.dispatch('organization/fetchOrganization', orgId)
                store.dispatch('organization/fetchMembers', orgId)
            }
            store.dispatch('materialBank/fetchPages')
            store.dispatch('discussions/fetchThreads')
            store.dispatch('directMessages/fetchMessages')
        })

        return {
            state,
            routerUserId,
            materialPages,
        }
    },
    mixins: [translator],
    methods: {
        rowSelected({ id, type }) {
            const haystack = type === "goal" ? this.$store.state.tasksGoals.userGoals : this.$store.state.tasksGoals.userTasks
            const needle = haystack.find((g) => g.id === id)
            this.state.openedGoalOrTask = needle
        },
        formatDate(dateStr) {
            return stringToDatetime(dateStr)
        }
    },
    computed: {
        // FIXME: move these to composition api
        userName() {
            return this.state.user ? this.state.user.first_name : ''
        },
        stateUserId() {
            return this.state.user ? this.state.user.id : ''
        },
        isAdminUser() {
            return this.$store.getters['user/isAdminUser']
        },
        orgUserName() {
            return (this.state.userAnswers && this.state.userAnswers.length) ? this.state.userAnswers[0].created_by.first_name : ''
        },
        invitations() {
            return this.$store.state.user.userSurveyTokens || []
        },
        userTaskRows() {
            return this.$store.state.tasksGoals.userTasks.map((ut) => {
                const title = ut.task[`title_${this.$i18n.locale}`] ?? ut.task["title_fi"]
                return [
                    {
                        text: title,
                        callback: this.rowSelected,
                        id: ut.id,
                        type: "task"
                    },
                    {
                        html: (ut.completed_at ? COMPLETED_CHECKBOX : INCOMPLETE_CHECKBOX),
                        align: "right"
                    }
                ]
            })
        },
        latestDiscussionEntries() {
            const currentUserId = this.state.user ? this.state.user.id : null

            const threadEntries = (this.$store.state.discussions.threads || []).map((thread) => ({
                id: `thread-${thread.id}`,
                date: thread.modified,
                title: thread.created_by ? thread.created_by.full_name : this.$t('default.discussions.unknown_author'),
                preview: thread.preview,
                to: { name: 'discussionThread', params: { threadId: thread.id } },
            }))

            const dmByConversation = new Map()
            for (const msg of this.$store.state.directMessages.messages || []) {
                const authorId = msg.author ? msg.author.id : msg.author_id
                const fromMe = authorId === currentUserId
                const otherUserId = fromMe ? (msg.recipient ? msg.recipient.id : null) : authorId
                const otherName = fromMe
                    ? (msg.recipient ? msg.recipient.full_name : '')
                    : (msg.author ? msg.author.full_name : '')
                const existing = dmByConversation.get(otherUserId)
                if (!existing || new Date(msg.created) > new Date(existing.date)) {
                    dmByConversation.set(otherUserId, {
                        id: `dm-${otherUserId}`,
                        date: msg.created,
                        title: otherName,
                        preview: msg.content,
                        to: { name: 'discussions' },
                    })
                }
            }

            return [...threadEntries, ...dmByConversation.values()]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 3)
        },
        userGoalRows() {
            return this.$store.state.tasksGoals.userGoals.map((ug) => {
                return [
                    {
                        text: ug.goal[`title_${this.$i18n.locale}`],
                        callback: this.rowSelected,
                        id: ug.id,
                        type: "goal"
                    },
                    {
                        html: (ug.completed ? COMPLETED_CHECKBOX : INCOMPLETE_CHECKBOX),
                        align: "right"
                    }
                ]
            })
        },
    }
}
</script>
